// SSR-safe viewport breakpoint detector (Phase 127 / PRIM-01, D-04).
//
// The single breakpoint contract for the responsive primitives: `useIsMobile()`
// returns `false` on the server snapshot and on the first client paint, then
// corrects to the live `matchMedia('(max-width: 767px)')` result and updates when
// the viewport crosses the `md` (768px) threshold. Returning a fixed desktop value
// for both the server snapshot and the first client read is what keeps hydration
// mismatch-free (D-04 + memory `rsc-function-prop-crash-runtime-only`): server HTML
// and first client render agree, the correction lands in a post-paint effect.
//
// This module is intentionally INDEPENDENT of `src/shared/ui/pwa/` (D-04): the
// matchMedia/SSR-guard shape is the same as `install-pwa-detect.ts` but it is NOT
// imported or refactored from there — the deferred generic `useMediaQuery`
// extraction is out of scope. Wave 2 (`DataList`, `ResponsiveDialog`) branches on
// this hook; pure presentational layout should prefer CSS `md:` utilities instead.
'use client';

import { useSyncExternalStore } from 'react';

/** `< md` (md = 768px). Must stay exactly in sync with the Tailwind md breakpoint. */
const QUERY = '(max-width: 767px)';

/**
 * Subscribe to viewport crossings of the md threshold. SSR-guarded: returns a
 * no-op cleanup when there is no `window` (never actually invoked on the server,
 * but keeps the function safe if pulled into a render path). Exported for tests.
 */
export function subscribe(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const mql = window.matchMedia(QUERY);
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}

/** Live client snapshot. Never called on the server (getServerSnapshot covers SSR). Exported for tests. */
export function getSnapshot(): boolean {
  return window.matchMedia(QUERY).matches;
}

/**
 * Server + first-client-paint snapshot: always desktop. Guarantees the server HTML
 * and the first client render agree (no hydration mismatch, D-04). Exported for tests.
 */
export function getServerSnapshot(): boolean {
  return false;
}

/**
 * `true` when the viewport is `< md` (max-width: 767px). SSR-safe: `false` on the
 * server and first client paint, then the live matchMedia result.
 */
export function useIsMobile(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
